import { supabase } from '../lib/supabase';

export const availabilityService = {
  /**
   * Get all availability slots for an institute
   */
  async getAvailability(instituteId) {
    const { data, error } = await supabase
      ?.from('institute_availability')
      ?.select('*')
      ?.eq('institute_id', instituteId)
      ?.eq('is_active', true)
      ?.order('day_of_week', { ascending: true })
      ?.order('start_time', { ascending: true });

    if (error) throw error;

    return data?.map(slot => ({
      id: slot?.id,
      instituteId: slot?.institute_id,
      dayOfWeek: slot?.day_of_week,
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      isActive: slot?.is_active,
      maxCounselors: slot?.max_counselors ?? 1,
      createdAt: slot?.created_at,
      updatedAt: slot?.updated_at
    }));
  },

  /**
   * Model B: Get available time slots for a specific date
   * Returns only slots that still have counselor capacity remaining
   */
  async getAvailableSlotsForDate(instituteId, date) {
    if (!instituteId || !date) return [];

    const dateObj = new Date(date);
    const dayOfWeek = dateObj?.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

    // Step 1: Get institute's availability slots for this day of week
    const { data: availSlots, error: availError } = await supabase
      ?.from('institute_availability')?.select('*')?.eq('institute_id', instituteId)?.eq('day_of_week', dayOfWeek)?.eq('is_active', true)?.order('start_time', { ascending: true });

    if (availError) throw availError;
    if (!availSlots || availSlots?.length === 0) return [];

    // Step 2: Get booking counts per time for this date
    const { data: bookedSlots, error: bookedError } = await supabase
      ?.from('counselor_bookings')?.select('booking_time')?.eq('institute_id', instituteId)?.eq('booking_date', date)?.in('status', ['confirmed', 'pending']);

    if (bookedError) throw bookedError;

    // Count bookings per time slot
    const bookingCounts = {};
    (bookedSlots || [])?.forEach(b => {
      const t = (b?.booking_time || '')?.substring(0, 5);
      bookingCounts[t] = (bookingCounts?.[t] || 0) + 1;
    });

    // Step 3: Return slots that still have capacity (bookings < maxCounselors)
    return availSlots?.filter(slot => {
        const slotTime = (slot?.start_time || '')?.substring(0, 5);
        const maxCounselors = slot?.max_counselors ?? 1;
        const booked = bookingCounts?.[slotTime] || 0;
        return booked < maxCounselors;
      })?.map(slot => {
        const slotTime = (slot?.start_time || '')?.substring(0, 5);
        const maxCounselors = slot?.max_counselors ?? 1;
        const booked = bookingCounts?.[slotTime] || 0;
        const remaining = maxCounselors - booked;
        return {
          id: slot?.id,
          instituteId: slot?.institute_id,
          dayOfWeek: slot?.day_of_week,
          startTime: slot?.start_time,
          endTime: slot?.end_time,
          maxCounselors,
          bookedCount: booked,
          remainingSpots: remaining,
          // Display label: "9:00 AM – 10:00 AM"
          label: `${formatTimeDisplay(slot?.start_time)} – ${formatTimeDisplay(slot?.end_time)}`,
          // Value used for booking_time column
          value: slot?.start_time
        };
      });
  },

  /**
   * Create a new availability slot
   */
  async createAvailability(availabilityData) {
    const { data, error } = await supabase
      ?.from('institute_availability')
      ?.insert({
        institute_id: availabilityData?.instituteId,
        day_of_week: availabilityData?.dayOfWeek,
        start_time: availabilityData?.startTime,
        end_time: availabilityData?.endTime,
        max_counselors: availabilityData?.maxCounselors ?? 1,
        is_active: true
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      instituteId: data?.institute_id,
      dayOfWeek: data?.day_of_week,
      startTime: data?.start_time,
      endTime: data?.end_time,
      maxCounselors: data?.max_counselors ?? 1,
      isActive: data?.is_active
    };
  },

  /**
   * Update max counselors for a slot
   */
  async updateMaxCounselors(slotId, maxCounselors) {
    const { data, error } = await supabase
      ?.from('institute_availability')
      ?.update({ max_counselors: maxCounselors, updated_at: new Date()?.toISOString() })
      ?.eq('id', slotId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      maxCounselors: data?.max_counselors
    };
  },

  /**
   * Delete an availability slot
   */
  async deleteAvailability(slotId) {
    const { error } = await supabase
      ?.from('institute_availability')
      ?.delete()
      ?.eq('id', slotId);

    if (error) throw error;

    return { success: true };
  },

  /**
   * Update availability slot active status
   */
  async toggleAvailability(slotId, isActive) {
    const { data, error } = await supabase
      ?.from('institute_availability')
      ?.update({ is_active: isActive, updated_at: new Date()?.toISOString() })
      ?.eq('id', slotId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      isActive: data?.is_active
    };
  },

  /**
   * Bulk create availability slots
   */
  async bulkCreateAvailability(instituteId, slots) {
    const slotsToInsert = slots?.map(slot => ({
      institute_id: instituteId,
      day_of_week: slot?.dayOfWeek,
      start_time: slot?.startTime,
      end_time: slot?.endTime,
      max_counselors: slot?.maxCounselors ?? 1,
      is_active: true
    }));

    const { data, error } = await supabase
      ?.from('institute_availability')
      ?.insert(slotsToInsert)
      ?.select();

    if (error) throw error;

    return data?.map(slot => ({
      id: slot?.id,
      instituteId: slot?.institute_id,
      dayOfWeek: slot?.day_of_week,
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      maxCounselors: slot?.max_counselors ?? 1,
      isActive: slot?.is_active
    }));
  }
};

// Helper: format "09:00:00" → "9:00 AM"
function formatTimeDisplay(time) {
  if (!time) return '';
  const [hours, minutes] = time?.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
}